import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Spinner } from '../../components/atoms/Spinner'
import { Th, Td } from '../../components/molecules/Table'
import { TableShell } from '../../components/molecules/TableShell'
import { Field } from '../../components/molecules/Field'
import { useUnits, type UnitDto } from '../../../viewmodels/useUnits'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

const NAME_MAX = 32

/**
 * /manage/units — catalog of unit labels (kg / piece / etc.) that
 * power the unit dropdowns on Raw Materials, Manage Purchases, and
 * Warehouse Product Purchases. Add + soft-delete; no edit (units are
 * immutable so historical unit_label snapshots aren't broken).
 */
export function UnitsPage(): JSX.Element {
  const vm = useUnits()
  const { can } = useAuth()
  const canAdd = can(SCREENCODES.MANAGEUNITS, 'add')
  const canDelete = can(SCREENCODES.MANAGEUNITS, 'delete')

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-heading text-h2 text-charcoal">Units</h1>
        <p className="font-body text-body-sm text-text-secondary mt-1">
          Units listed here populate the single-select unit dropdown on every screen that
          captures a unit (raw materials + all three Purchases screens). Units are
          immutable — to retire one, soft-delete it; existing rows that snapshotted the
          name string remain intact.
        </p>
      </header>

      {vm.error !== null ? (
        <Alert tone="error">
          {vm.error}{' '}
          <button type="button" className="underline underline-offset-2 hover:text-charcoal" onClick={() => vm.reload()}>
            Retry
          </button>
        </Alert>
      ) : null}

      {canAdd ? <AddForm onAdd={vm.add} /> : null}

      {vm.isLoading ? (
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      ) : vm.items.length === 0 ? (
        <EmptyState />
      ) : (
        <Table items={vm.items} canDelete={canDelete} onDelete={vm.remove} />
      )}
    </div>
  )
}

function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No units yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Add your first unit above (e.g. <span className="font-mono">kg</span>, <span className="font-mono">piece</span>).
      </p>
    </div>
  )
}

function AddForm({
  onAdd
}: {
  readonly onAdd: (name: string) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}): JSX.Element {
  const [name, setName] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const handleSubmit = async (): Promise<void> => {
    setFieldError(null)
    setFormError(null)
    const trimmed = name.trim()
    if (trimmed === '' || trimmed.length > NAME_MAX) {
      setFieldError(`Unit must be 1..${NAME_MAX} chars`)
      return
    }
    setSubmitting(true)
    const r = await onAdd(trimmed)
    setSubmitting(false)
    if (!r.ok) {
      if (r.field === 'name') setFieldError(r.message)
      else setFormError(r.message)
      return
    }
    setName('')
  }

  return (
    <div className="border border-border rounded-md bg-parchment/40 p-4 space-y-3">
      <p className="font-body text-body-sm font-medium text-charcoal">Add a new unit</p>
      {formError !== null ? <Alert tone="error">{formError}</Alert> : null}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
        <Field label="Unit name" error={fieldError}>
          <Input
            value={name}
            hasError={fieldError !== null}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. kg, piece, metre, sq. ft."
            maxLength={NAME_MAX}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleSubmit()
              }
            }}
          />
        </Field>
        <Button onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  )
}

function Table({
  items,
  canDelete,
  onDelete
}: {
  readonly items: UnitDto[]
  readonly canDelete: boolean
  readonly onDelete: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}): JSX.Element {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const handleDelete = async (id: string, name: string): Promise<void> => {
    if (typeof window !== 'undefined' && !window.confirm(`Delete unit "${name}"?`)) return
    setBusyId(id)
    setRowError(null)
    const r = await onDelete(id)
    setBusyId(null)
    if (!r.ok) setRowError(r.message)
  }

  return (
    <div className="space-y-3">
      {rowError !== null ? <Alert tone="error">{rowError}</Alert> : null}
      <TableShell>
        <table className="w-full font-body text-body-sm">
          <thead className="bg-parchment/50 text-text-secondary">
            <tr>
              <Th>Unit</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-border">
                <Td className="font-mono">{row.name}</Td>
                <Td className="text-right whitespace-nowrap">
                  {canDelete ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => void handleDelete(row.id, row.name)}
                      disabled={busyId === row.id}
                    >
                      {busyId === row.id ? '…' : 'Delete'}
                    </Button>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
    </div>
  )
}
